import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./decisionList.css";
import Table from "../../core/common/dataTable/index";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { AxiosError } from "axios";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import withPermission from "../../core/common/autorisations/withPermission";
import { my_routes } from "../router/all_routes";
import {
  deleteDecisionStudent,
  deleteDecisionValidation,
  generateDecisionPDF,
  generateDiplomesByDecision,
  getDecisionById,
  getDecisionParameter,
  updateDecision,
} from "../../api/diplome";
import { toast } from "sonner";
import AddStudentToDecisionModal from "./AddStudentToDecisionModal";
import ValidationModal from "./validationModal";
import { useSelector } from "react-redux";
import ValidationLevelCard from "./validationLevelCard";
import PrintDecision from "./printDecision";
import { reCheckStudentEligibility } from "../../api/notes";

// ─── Boutons protégés ────────────────────────────────────────────────────────

const ProtectedBtnDeleteDecisionStudent = withPermission(
  (props: any) => (
    <li>
      <Link
        onClick={() => props.deleteDecisionStudentAlert(props.record)}
        className="dropdown-item rounded-1"
        to="#"
      >
        <i className="ti ti-trash-x me-2" />
        Supprimer
      </Link>
    </li>
  ),
  [buildPermission(Resource.DIPLOME, Action.UPDATE)],
  "masquer",
);

const ProtectedBtnGenerateDiplome = withPermission(
  (props: any) => (
    <li>
      <button
        type="button"
        className="btn btn-success me-2 d-inline-flex align-items-center"
        onClick={() => props.generateDiplomesAlert()}
      >
        <i className="ti ti-certificate me-2" />
        Générer les diplômes
      </button>
    </li>
  ),
  [buildPermission(Resource.DIPLOME, Action.UPDATE)],
  "masquer",
);

const ProtectedBtnPrintDecision = withPermission(
  (props: any) => (
    <div className="d-flex align-items-center align-self-center">
      <button
        className="btn btn-light btn-sm me-2 d-inline-flex align-items-center"
        title="Générer le planning"
        onClick={props.generateDecision}
        disabled={props.isPrintLoading}
      >
        {props.isPrintLoading ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            />
            Génération en cours...
          </>
        ) : (
          <>
            <i className="ti ti-printer me-2" /> Imprimer la décision
          </>
        )}
      </button>
    </div>
  ),
  [buildPermission(Resource.DIPLOME, Action.UPDATE)],
  "masquer",
);

const getStatutBadge = (statut: string) => {
  switch (statut) {
    case "Finalisé":
      return "badge badge-success d-inline-flex align-items-center";
    case "En cours de validation":
      return "badge badge-warning d-inline-flex align-items-center";
    case "Validé Partiellement":
      return "badge badge-info d-inline-flex align-items-center";
    case "Brouillon":
    default:
      return "badge badge-light d-inline-flex align-items-center text-muted";
  }
};

const getStatutIcon = (statut: string) => {
  switch (statut) {
    case "Finalisé":
      return "ti-circle-check";
    case "En cours de validation":
      return "ti-clock";
    case "Validé Partiellement":
      return "ti-circle-x";
    case "Brouillon":
    default:
      return "ti-clock-pause";
  }
};

// ─── Composant principal ──────────────────────────────────────────────────────

const DecisionDetails = () => {
  const navigate = useNavigate();
  const route = my_routes;

  const [decisionDetail, setDecisionDetail] = useState<any>(null);
  const [decisionParameter, setDecisionParameter] = useState<any>(null);
  const [allStudentDecisions, setAllStudentDecisions] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPrintLoading, setIsPrintLoading] = useState(false);
  const [isCheckLoading, setIsCheckLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [selectedValidationLevel, setSelectedValidationLevel] =
    useState<string>("");
  const [showValidationModal, setShowValidationModal] = useState(false);

  // state pour le PDF
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [showPrintDecisionModal, setShowPrintDecisionModal] = useState(false);

  // === Paramètres d'URL ===
  const { id } = useParams(); // On récupère l'id de l'URL
  const userData = useSelector((state: any) => state.auth.user);

  const MySwal = withReactContent(Swal);

  const openValidationModal = (level: string) => {
    setSelectedValidationLevel(level);
    setShowValidationModal(true);
  };

  // ── Colonnes du tableau ──────────────────────────────────────────────────

  const columns = [
    {
      title: "Matricule",
      dataIndex: "matricule",
      render: (_: unknown, record: any) => (
        <span
          className="fw-medium"
          style={{
            fontFamily: "monospace",
            fontSize: 13,
            background: "#eef4ff",
            color: "#1d4ed8",
            padding: "4px 10px",
            borderRadius: 999,
            border: "1px solid #dbe7ff",
            letterSpacing: "0.03em",
          }}
        >
          {record?.StudentProfil?.matricule || "--"}
        </span>
      ),
    },
    {
      title: "Nom et Prénom",
      dataIndex: "nomPrenom",
      render: (_: unknown, record: any) => (
        <span style={{ fontSize: 13 }} className="fw-medium">
          {record?.StudentProfil?.firstname && record?.StudentProfil?.lastname
            ? `${record.StudentProfil.firstname} ${record.StudentProfil.lastname}`
            : "--"}
        </span>
      ),
    },
    {
      title: "Classe / Parcours",
      dataIndex: "classeParcours",
      render: (_: unknown, record: any) => (
        <span style={{ fontSize: 13 }} className="fw-bold">
          {record?.ParcoursStudent?.Parcour?.Cycle?.label || "--"} -{" "}
          {record?.ParcoursStudent?.Parcour?.title || "--"}
          <span className="d-block fw-medium" style={{ fontSize: 12 }}>
            {record?.Class?.Level?.abbreviation || "--"}{" "}
            {record?.Class?.label || "--"}
          </span>
        </span>
      ),
    },
    {
      title: "Mention",
      dataIndex: "mention",
      render: (_: unknown, record: any) => (
        <span style={{ fontSize: 13 }} className="fw-medium">
          {record?.average_cycle} - {record?.mention_cycle || "--"}
        </span>
      ),
    },
    {
      title: "Éligibilité",
      dataIndex: "eligibility",
      render: (_: unknown, record: any) => (
        <span
          className={`badge ${
            record?.is_eligible ? "bg-soft-success" : "bg-soft-danger"
          }`}
          style={{ fontSize: 12 }}
        >
          {record?.is_eligible ? "Éligible" : "Inéligible"}
        </span>
      ),
    },
    ...(decisionDetail?.statut === "Brouillon"
      ? [
          {
            title: "Action",
            dataIndex: "action",
            render: (_: unknown, record: any) => (
              <div className="d-flex align-items-center">
                <div className="dropdown">
                  <Link
                    to="#"
                    className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i className="ti ti-dots-vertical fs-14" />
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-right p-3">
                    {/* Supprimer (protégé) */}
                    <ProtectedBtnDeleteDecisionStudent
                      deleteDecisionStudentAlert={deleteDecisionStudentAlert}
                      record={record}
                    />
                  </ul>
                </div>
              </div>
            ),
          },
        ]
      : []),
  ];

  // ── Suppression ──────────────────────────────────────────────────────────

  const deleteValidationAlert = (validation: any) => {
    MySwal.fire({
      title: "Supprimer cette validation ?",
      text: `Souhaitez-vous vraiment supprimer la validation de niveau ${validation?.level_of_validation} ?`,
      showCancelButton: true,
      confirmButtonText: "Supprimer !",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // ── Affiche le spinner pendant la requête ──
        MySwal.fire({
          title: "Suppression en cours...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => MySwal.showLoading(),
        });

        try {
          await deleteDecisionValidation(
            validation?.id,
            buildPermissionContext(Resource.DIPLOME, Action.UPDATE),
          );
          MySwal.fire({
            title: "Validation supprimée avec succès !",
            icon: "success",
            confirmButtonText: "OK",
          });
          fetchDecisionDetails(false, ["validations", "status"]);
        } catch (error) {
          const err = error as AxiosError<any>;
          MySwal.fire({
            title: "Erreur",
            text:
              err.response?.data?.message ||
              err.response?.data?.error ||
              "Une erreur est survenue, réessayez plus tard",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const deleteDecisionStudentAlert = (decisionStudent: any) => {
    MySwal.fire({
      title: "Supprimer un étudiant de la décision ?",
      html: (
        <span>
          Souhaitez-vous vraiment supprimer l&apos;étudiant{" "}
          <strong>
            {decisionStudent?.StudentProfil?.firstname}{" "}
            {decisionStudent?.StudentProfil?.lastname}
          </strong>{" "}
          de la décision ?
        </span>
      ),
      showCancelButton: true,
      confirmButtonText: "Supprimer !",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // ── Affiche le spinner pendant la requête ──
        MySwal.fire({
          title: "Suppression en cours...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => MySwal.showLoading(),
        });

        try {
          await deleteDecisionStudent(
            decisionStudent?.id,
            buildPermissionContext(Resource.DIPLOME, Action.UPDATE),
          );
          MySwal.fire({
            title: "Étudiant supprimé avec succès !",
            icon: "success",
            confirmButtonText: "OK",
          });
          fetchDecisionDetails(false, ["students"]);
        } catch (error) {
          const err = error as AxiosError<any>;
          MySwal.fire({
            title: "Erreur",
            text:
              err.response?.data?.message ||
              err.response?.data?.error ||
              "Une erreur est survenue, réessayez plus tard",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  // ── Modification ──────────────────────────────────────────────────────────

  const updateDecisionAlert = (statut: string) => {
    MySwal.fire({
      title:
        statut === "Brouillon"
          ? "Annuler la soumission de la décision pour validation ?"
          : "Soumettre la décision pour validation ?",
      text: `Veuillez vous assurer de la conformité des informations pour la validation.`,
      showCancelButton: true,
      confirmButtonText: statut === "Brouillon" ? "Annuler !" : "Soumettre !",
      cancelButtonText: statut === "Brouillon" ? "Retour" : "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // ── Affiche le spinner pendant la requête ──
        MySwal.fire({
          title:
            statut === "Brouillon"
              ? "Annulation en cours..."
              : "Soumission en cours...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => MySwal.showLoading(),
        });

        try {
          await updateDecision(
            id,
            { statut: statut },
            buildPermissionContext(Resource.DIPLOME, Action.UPDATE),
          );
          MySwal.fire({
            title:
              statut === "Brouillon"
                ? "Décision annulée avec succès !"
                : "Décision soumise pour validation avec succès !",
            icon: "success",
            confirmButtonText: "OK",
          });
          fetchDecisionDetails(false, ["validations", "status"]);
        } catch (error) {
          const err = error as AxiosError<any>;
          MySwal.fire({
            title: "Erreur",
            text:
              err.response?.data?.message ||
              err.response?.data?.error ||
              "Une erreur est survenue, réessayez plus tard",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  const generateDiplomesAlert = () => {
    MySwal.fire({
      title: "Générer les diplômes pour cette décision ?",
      text: `Veuillez vous assurer de la conformité des informations pour la génération des diplômes.`,
      showCancelButton: true,
      confirmButtonText: "Générer !",
      cancelButtonText: "Annuler",
    }).then(async (result) => {
      if (result.isConfirmed) {
        // ── Affiche le spinner pendant la requête ──
        MySwal.fire({
          title: "Génération en cours...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => MySwal.showLoading(),
        });

        try {
          const response = await generateDiplomesByDecision(
            id,
            buildPermissionContext(Resource.DIPLOME, Action.CREATE),
          );
          // console.log("Réponse de génération : ", response);
          MySwal.fire({
            title: "Diplômes générés avec succès !",
            icon: "success",
            confirmButtonText: "OK",
          });
          fetchDecisionDetails(false, ["diplomes"]);
        } catch (error) {
          const err = error as AxiosError<any>;
          MySwal.fire({
            title: "Erreur",
            text:
              err.response?.data?.message ||
              err.response?.data?.error ||
              "Une erreur est survenue, réessayez plus tard",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  // ── Chargement des données ───────────────────────────────────────────────
  type DecisionRefreshScope =
    | "all"
    | "students"
    | "validations"
    | "status"
    | "diplomes";

  const fetchDecisionDetails = async (
    showPageLoader = false,
    scopes: DecisionRefreshScope[] = ["all"],
  ) => {
    try {
      if (showPageLoader) {
        setIsLoading(true);
      }

      const response = await getDecisionById(
        id,
        buildPermissionContext(Resource.DIPLOME, Action.READ),
      );
      const decisionData = response.data || {};

      if (scopes.includes("all")) {
        setDecisionDetail(decisionData);
        setAllStudentDecisions(decisionData.DecisionStudents || []);
      }

      if (scopes.includes("students")) {
        setAllStudentDecisions(decisionData.DecisionStudents || []);

        setDecisionDetail((prev: any) => ({
          ...prev,
          total_students: decisionData.total_students,
        }));
      }

      if (scopes.includes("validations")) {
        setDecisionDetail((prev: any) => ({
          ...prev,
          statut: decisionData.statut,
          DecisionValidations: decisionData.DecisionValidations || [],
        }));
      }

      if (scopes.includes("status")) {
        setDecisionDetail((prev: any) => ({
          ...prev,
          statut: decisionData.statut,
        }));
      }

      if (scopes.includes("diplomes")) {
        setDecisionDetail((prev: any) => ({
          ...prev,
          diplome_generate: decisionData.diplome_generate,
        }));
      }
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      if (showPageLoader) {
        setIsLoading(false);
      }

      setHasLoaded(true);
    }
  };

  const fetchDecisionParameter = async () => {
    try {
      const response = await getDecisionParameter(
        buildPermissionContext(Resource.DIPLOME, Action.READ),
      );
      if (!response) {
        // Si aucune année trouvé
        throw new Error("Paramètres introuvable");
      }
      // console.log("Paramètres", response.data)
      setDecisionParameter(response.data.decisionParameter);
    } catch (error: any) {
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // Fontion pour générer une décision
  const generateDecision = async () => {
    try {
      setIsPrintLoading(true);
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
        setPdfBlobUrl(null);
      }

      const response = await generateDecisionPDF(
        id,
        buildPermissionContext(Resource.DIPLOME, Action.CREATE),
      );
      const blob = new Blob([response], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setShowPrintDecisionModal(true);

      toast.success("Décision générée avec succès");
    } catch (error: any) {
      // console.log("erreur génération de la décision: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setIsPrintLoading(false); // ← Toujours arrêter le chargement à la fin
    }
  };

  const reCheckStudentEligibilityDecision = async () => {
    try {
      setIsCheckLoading(true);

      const response = await reCheckStudentEligibility(
        id,
        buildPermissionContext(Resource.DIPLOME, Action.UPDATE),
      );
      // console.log("Réponse de réexamen de l'éligibilité : ", response);

      fetchDecisionDetails(false, ["students"]);
      toast.success("Éligibilité réexaminée avec succès");
    } catch (error: any) {
      // console.log("erreur de réexamen de l'éligibilité: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setIsCheckLoading(false); // ← Toujours arrêter le chargement à la fin
    }
  };

  useEffect(() => {
    // On attend que id soit défini
    if (!id) return;

    fetchDecisionDetails(true, ["all"]);
    fetchDecisionParameter();
  }, [id]);

  const validationLevel1 = useMemo(
    () =>
      decisionDetail?.DecisionValidations?.find(
        (v: any) => v.level_of_validation === "1",
      ),
    [decisionDetail],
  );

  const validationLevel2 = useMemo(
    () =>
      decisionDetail?.DecisionValidations?.find(
        (v: any) => v.level_of_validation === "2",
      ),
    [decisionDetail],
  );

  // ── Rendu ────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="page-wrapper">
        <div
          className="content d-flex align-items-center justify-content-center"
          style={{ minHeight: "calc(100vh - 120px)" }}
        >
          <div className="card">
            <div className="card-body py-5 text-center">
              <span
                className="spinner-border text-primary me-2"
                role="status"
              />
              Chargement de la décision...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!decisionDetail || decisionDetail.length === 0) {
    return <div className="alert alert-info">Aucune décision trouvée.</div>;
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content decision-list-page">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Décisions de diplomation</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={route.decisionList}>Diplômes</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Décisions #{decisionDetail?.number}
                  </li>
                </ol>
              </nav>
            </div>
            {/* Boutton en entête de la page */}
            {/* <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <ProtectedBtnAddAcademicYear setShowCreate={setShowCreate} />
            </div> */}
          </div>
          {/* /Page Header */}
          <div className="row g-3">
            <div className="card col-12 mb-0">
              <div className="card-header d-flex align-items-center justify-content-between p-3 pb-0">
                <div>
                  <h3 className=" mb-1"> Décision {decisionDetail?.number} </h3>
                  <p className="d-flex align-items-center mb-2 text-muted">
                    <i className="ti ti-calendar-bolt me-1 mb-1" />
                    Année :{" "}
                    <span className="text-dark ms-1">
                      {" "}
                      Session de Juin{" "}
                      {new Date(decisionDetail?.createdAt).getFullYear()}
                    </span>
                    <i className="ti ti-user ms-2 me-1 mb-1" />{" "}
                    <span className="text-dark ms-1">
                      {" "}
                      {decisionDetail?.total_students} Étudiant(s)
                    </span>
                  </p>
                </div>
                <ProtectedBtnPrintDecision
                  generateDecision={generateDecision}
                  isPrintLoading={isPrintLoading}
                />
                {/* <div className="d-flex align-items-center align-self-center">
                          <button
                              className="btn btn-light btn-sm me-2 d-inline-flex align-items-center"
                              title="Générer le planning"
                              onClick={generateDecision}
                              disabled={isPrintLoading}
                          >
                              {isPrintLoading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                  Génération en cours...
                                </>
                              ) : (
                                <>
                                  <i className="ti ti-printer me-2" /> Imprimer la décision
                                </>
                              )}
                          </button>

                      </div> */}
              </div>
              <div className="card-body p-0">
                <div className="ticket-information ticket-details">
                  <div className="d-flex align-items-center justify-content-between flex-wrap p-3 pb-0 border-bottom">
                    <div className="d-flex align-items-center flex-wrap">
                      <div className="d-flex align-items-center mb-2">
                        <p className="d-flex align-items-center mb-1 me-2">
                          <i className="ti ti-calendar-bolt me-1" />
                          Créée le :{" "}
                          {new Date(
                            decisionDetail?.createdAt,
                          ).toLocaleDateString("fr-FR", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </p>
                        <p className="d-flex align-items-center mb-1">
                          <i className="ti ti-user me-1" />
                          Par : {decisionDetail?.UserProfil?.firstname}{" "}
                          {decisionDetail?.UserProfil?.lastname}
                        </p>
                      </div>
                    </div>

                    <div className="d-flex align-items-center mb-4 me-3">
                      <span className={getStatutBadge(decisionDetail?.statut)}>
                        <i
                          className={`ti ${getStatutIcon(decisionDetail?.statut)} me-1`}
                        />
                        {decisionDetail?.statut}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {decisionDetail?.statut !== "Brouillon" && (
            <div className="row gx-4 gy-3 mt-1 mb-0">
              <ValidationLevelCard
                level="1"
                validation={validationLevel1}
                validatorParam={decisionParameter?.validator1}
                currentUserId={userData?.id}
                hasLoaded={hasLoaded}
                onValidate={openValidationModal}
                deleteValidation={deleteValidationAlert}
                onSaved={() =>
                  fetchDecisionDetails(false, ["validations", "status"])
                }
              />
              <ValidationLevelCard
                level="2"
                validation={validationLevel2}
                validatorParam={decisionParameter?.validator2}
                currentUserId={userData?.id}
                hasLoaded={hasLoaded}
                onValidate={openValidationModal}
                deleteValidation={deleteValidationAlert}
                onSaved={() =>
                  fetchDecisionDetails(false, ["validations", "status"])
                }
              />
            </div>
          )}

          {/* ── Tableau ── */}
          <div
            className={`card ${decisionDetail?.statut == "Brouillon" && "mt-3"}`}
          >
            <div className="decision-table-header card-header d-flex align-items-center justify-content-between flex-wrap">
              <div>
                <h4 className="mb-1">Étudiants</h4>
                <p className="mb-0">
                  Une lecture plus claire des statuts, validations et cohortes.
                </p>
              </div>
              {validationLevel1 && validationLevel2 && (
                <>
                  {!decisionDetail?.diplome_generate ? (
                    <ProtectedBtnGenerateDiplome
                      generateDiplomesAlert={generateDiplomesAlert}
                    />
                  ) : (
                    <Link
                      to={`/diplomes/${decisionDetail?.id}`}
                      type="button"
                      className="btn btn-primary me-2 d-inline-flex align-items-center"
                      // onClick={handleSubmitForValidation}
                    >
                      <i className="ti ti-certificate me-2" />
                      Voir les diplômes
                    </Link>
                  )}
                </>
              )}
              {decisionDetail.statut === "Brouillon" && (
                <div>
                  <button
                    className="btn btn-success btn-sm me-2 d-inline-flex align-items-center"
                    title="Vérifier l'éligibilité des étudiants"
                    onClick={reCheckStudentEligibilityDecision}
                  >
                    {isCheckLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Réexamen en cours...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-refresh me-2" /> Lancer la
                        vérification
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-info btn-sm me-2 d-inline-flex align-items-center"
                    title="Générer le planning"
                    onClick={() => setShowAddStudent(true)}
                  >
                    <i className="ti ti-plus me-2" /> Ajouter un étudiant
                  </button>
                </div>
              )}
            </div>
            <div className=" card-body p-0 py-3">
              {isLoading && !hasLoaded ? (
                <div className="text-center py-4">
                  <div className="spinner-border" role="status" />
                  <span className="visually-hidden">
                    Chargement des étudiants...
                  </span>
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={allStudentDecisions}
                  Selection={true}
                  // onRow={(record: DecisionTable) => ({
                  //   onClick: () => navigate(`/diplomes/decisions/${record.id}`),
                  //   style: { cursor: "pointer" },
                  // })}
                />
              )}
            </div>

            {decisionDetail.statut === "Brouillon" && (
              <div className="card-footer d-flex justify-content-end align-items-center">
                <button
                  type="button"
                  className="btn btn-primary d-inline-flex align-items-center"
                  onClick={() => updateDecisionAlert("En cours de validation")}
                >
                  <i className="ti ti-send me-2" />
                  Soumettre pour validation
                </button>
              </div>
            )}

            {decisionDetail.statut === "En cours de validation" &&
              !validationLevel1 &&
              !validationLevel2 && (
                <div className="card-footer d-flex justify-content-end align-items-center">
                  <button
                    type="button"
                    className="btn btn-danger d-inline-flex align-items-center"
                    onClick={() => updateDecisionAlert("Brouillon")}
                  >
                    <i className="ti ti-x me-2" />
                    Annuler la soumission
                  </button>
                </div>
              )}
          </div>

          {/* ── Modal de validation ── */}
          <ValidationModal
            show={showValidationModal}
            idDecision={id}
            validationLevel={selectedValidationLevel}
            onClose={() => setShowValidationModal(false)}
            onSaved={() =>
              fetchDecisionDetails(false, ["validations", "status"])
            }
          />
        </div>
      </div>

      {/* ── Modal création ── */}
      <AddStudentToDecisionModal
        show={showAddStudent}
        idDecision={id}
        onClose={() => setShowAddStudent(false)}
        onSaved={() => fetchDecisionDetails(false, ["students"])}
      />

      <PrintDecision
        show={showPrintDecisionModal}
        metaData={{
          pdfBlobUrl,
          decision_number: decisionDetail?.number,
        }}
        onClose={() => setShowPrintDecisionModal(false)}
      />
    </>
  );
};

export default DecisionDetails;
